import React, { useState, useEffect } from 'react';
import Header from './components/Header';
import RoomSelection from './components/RoomSelection';
import Booking from './components/Booking';
import { BookingProvider } from './context/BookingContext';
import WidgetLoader from './components/WidgetLoader';
import PaymentSuccessModal from './components/PaymentSuccessModal';

function App() {
  const [currentPage, setCurrentPage] = useState('index');
  const [paymentResult, setPaymentResult] = useState(null);

  // Function to extract route from hash
  const getRouteFromHash = () => {
    const hash = window.location.hash;
    if (hash === '#/booking') return 'booking';
    return 'index';
  };

  useEffect(() => {
    // Check URL for widget parameters AND payment status
    const searchParams = new URLSearchParams(window.location.search);
    const fromWidget = searchParams.get('from_widget');
    const paymentStatus = searchParams.get('payment_status');
    
    console.log('🏠 App mounted. URL params:', {
      fromWidget,
      paymentStatus,
      hotelName: searchParams.get('hotel_name'),
      hasSearch: !!window.location.search,
      hash: window.location.hash,
      pathname: window.location.pathname
    });
    
    // Handle payment callback first
    if (paymentStatus) {
      const result = {
        status: paymentStatus,
        bookingReference: searchParams.get('booking_reference'),
        reservationId: searchParams.get('reservation_id'),
        message: searchParams.get('message')
      };
      
      console.log('💰 Payment callback detected:', result);
      setPaymentResult(result);
      
      // Always go to home page after payment
      setCurrentPage('index');
      
      // Clean URL but preserve widget params
      if (window.history.replaceState) {
        let cleanUrl = '/';
        if (fromWidget) {
          cleanUrl += `?from_widget=${fromWidget}`;
          const hotelName = searchParams.get('hotel_name');
          const hotelId = searchParams.get('hotel_id');
          if (hotelName) cleanUrl += `&hotel_name=${hotelName}`;
          if (hotelId) cleanUrl += `&hotel_id=${hotelId}`;
        }
        window.history.replaceState({}, document.title, cleanUrl);
        console.log('🧹 Cleaned URL after payment');
      }
      
    } else if (fromWidget === 'true') {
      // Widget flow - stay on home page
      setCurrentPage('index');
      
      // Don't clean URL here - WidgetLoader needs these params
      // Just ensure we're on the root path with widget params
      if (window.history.replaceState && window.location.pathname !== '/') {
        const params = new URLSearchParams(window.location.search);
        window.history.replaceState({}, document.title, '/?' + params.toString());
      }
      
    } else {
      // Normal routing - Check multiple sources
      const path = window.location.pathname;
      const hash = window.location.hash;
      
      console.log('Routing check:', { path, hash });
      
      // Priority: Hash > Pathname > Default to index
      if (hash === '#/booking') {
        setCurrentPage('booking');
        console.log('Route set from hash: booking');
      } else if (path.includes('booking')) {
        setCurrentPage('booking');
        console.log('Route set from pathname: booking');
        
        // If accessing via pathname, convert to hash for static hosting
        if (window.history.replaceState && !hash) {
          const params = window.location.search;
          window.history.replaceState({}, document.title, '/#' + path + params);
          console.log('Converted pathname to hash');
        }
      } else {
        setCurrentPage('index');
        console.log('Route set: index');
      }
    }
  }, []);

  // Handle browser back/forward buttons
  useEffect(() => {
    const handlePopState = () => {
      const route = getRouteFromHash();
      setCurrentPage(route);
      console.log('Popstate: route changed to', route);
    };
    
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // Handle hash changes
  useEffect(() => {
    const handleHashChange = () => {
      const route = getRouteFromHash();
      setCurrentPage(route);
      console.log('Hashchange: route changed to', route);
    };
    
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  const navigateTo = (page) => {
    console.log('Navigating to:', page);
    setCurrentPage(page);
    
    // Get current search params (preserves widget params if any)
    const searchParams = new URLSearchParams(window.location.search);
    const searchString = searchParams.toString();
    
    if (page === 'booking') {
      // Navigate with hash-based routing
      const newUrl = searchString ? `/#/booking?${searchString}` : '/#/booking';
      window.history.pushState({}, '', newUrl);
      console.log('Pushed state:', newUrl);
    } else {
      // Navigate to home, preserve widget params
      const newUrl = searchString ? `/?${searchString}` : '/';
      window.history.pushState({}, '', newUrl);
      console.log('Pushed state:', newUrl);
    }
  };

  const handleClosePaymentModal = () => {
    setPaymentResult(null);
  };

  return (
    <BookingProvider>
      <WidgetLoader />
      
      <div className="min-h-screen bg-white text-text-primary font-sans">
        <Header />
        <main className="">
          {currentPage === 'index' ? (
            <RoomSelection navigateTo={navigateTo} />
          ) : (
            <Booking navigateTo={navigateTo} />
          )}
        </main>
        
        {/* Payment Success/Failure Modal */}
        {paymentResult && (
          <PaymentSuccessModal 
            result={paymentResult}
            onClose={handleClosePaymentModal}
          />
        )}
      </div>
    </BookingProvider>
  );
}

export default App;